import { useState, useEffect } from 'react';
import {
  Dialog, IconButton,
  InputAdornment,
  List as MuiList, ListItem, ListItemButton,
  ListItemIcon, ListItemText, TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { Project } from 'lib/types/projects';
import Link from 'next/link';
import { QuickAddProject } from 'components/AddEditProject/AddEditProject';
import { pluralize } from 'lib/helpers';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface Props {
  postIds: number[];
  onClose: () => void;
}

export const BulkSavePostsToProjectsModal = ({
  postIds, onClose,
}: Props) => {
  const currentUser = useCurrentUser();
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [isSendingData, setIsSendingData] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    data: projects, isLoading: isLoadingProjects,
    isPending: isPendingProjects, error: projectsError,
  } = useQuery({
    queryKey: ['getProjects', currentUser?.id],
    queryFn: () => API.getProjects(undefined),
    enabled: !!currentUser,
  });

  useEffect(() => {
    if (projectsError) {
      enqueueSnackbar(`Error loading projects: ${projectsError}`, {
        variant: 'error',
      });
    }
  }, [projectsError, enqueueSnackbar]);

  const addToProject = async (project: Project) => {
    try {
      setIsSendingData(true);
      await API.addPostsToProject(project.id, postIds);

      // Invalidate getProjectsForPost queries for all posts that were added
      postIds.forEach((postId) => {
        queryClient.invalidateQueries({
          queryKey: ['getProjectsForPost', postId],
        });
      });

      setIsSendingData(false);
      onClose();
      enqueueSnackbar(`Added to ${project.name}!`, {
        variant: 'success',
        autoHideDuration: 9000,
        action: (
          <Link href={`/projects/${project.publicId}`}>
            View project
          </Link>
        ),
      });
    } catch (error) {
      enqueueSnackbar(`Error adding posts to project: ${error}`, {
        variant: 'error',
      });
    }
  };

  const save = async (project: Project) => {
    if (!project.id) return;
    addToProject(project);
  };

  const handleQuickAddProjectSave = () => {
    // Invalidate getProjectsForPost queries for all posts that were added to the new project
    postIds.forEach((postId) => {
      queryClient.invalidateQueries({
        queryKey: ['getProjectsForPost', postId],
      });
    });
    onClose();
  };

  const filterItemsBySearch = (items: Project[], searchText: string) => {
    if (!searchText) return items;
    const query = searchText.toLowerCase();
    return items.filter((item) => {
      if (item.name) return item.name.toLowerCase().includes(query);
      if (item.team?.name) return item.team.name.toLowerCase().includes(query);
      return false;
    });
  };

  const [searchQuery, setSearchQuery] = useState('');
  const filteredProjectsBySearch = filterItemsBySearch(
    projects || [], searchQuery,
  ).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <div className="absolute right-2 top-2 text-orange">
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <div className="p-4">
        <>
          {open ? (
            <QuickAddProject
              onCancel={() => setOpen(false)}
              onSave={handleQuickAddProjectSave}
              project={{
                postIds,
              }}
            />
          )
            : (
              <>
                <div className="flex flex-wrap sm:flex-nowrap items-center">
                  <h1 className="text-lg text-orange font-bold mr-5">
                    Save
                    {' '}
                    {postIds.length}
                    {' '}
                    {pluralize('Post', postIds.length)}
                    {' '}
                    to a project
                  </h1>
                </div>
                <div>
                  <div className="p-2 sticky top-0 bg-white dark:bg-appbg-dark z-10">
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search projects"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        endAdornment: searchQuery && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchQuery('');
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  <MuiList>
                    {!projects?.length
                    && !isLoadingProjects
                    && !isPendingProjects && (
                      <div className="text-center p-2">
                        <p className="mb-2">
                          No projects found.
                        </p>
                        <Link className="btn-primary" href="/projects/new">
                          Create a project
                        </Link>
                      </div>
                    )}
                    {(isLoadingProjects || isPendingProjects) && (
                      <div className="text-center p-2">
                        Loading your projects...
                      </div>
                    )}
                    {filteredProjectsBySearch.map((project) => (
                      <ListItem key={project.id} disablePadding>
                        <ListItemButton
                          disabled={isSendingData}
                          onClick={() => save(project)}
                        >
                          <ListItemIcon>
                            <AddIcon color="inherit" />
                          </ListItemIcon>
                          <ListItemText>
                            <div>
                              {project.name}
                              {project.team?.name && (
                                <span className="font-light text-xs ml-2">
                                  (
                                  {project.team.name}
                                  )
                                </span>
                              )}
                            </div>
                          </ListItemText>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </MuiList>
                </div>
                <div>
                  <button type="button" className="py-4 w-full hover:bg-hintgray hover:dark:bg-darkgray" onClick={() => setOpen(true)}>
                    <AddIcon />
                    {' '}
                    Create a New Project
                  </button>
                </div>
              </>
            )}
        </>
      </div>
    </Dialog>
  );
};
